<template>
  <div class="me-panel">
    <div v-if="!hasContent" class="me-empty">
      <span class="me-empty-text">Marketing email not generated yet.</span>
    </div>

    <template v-else>
      <div class="me-row">
        <div class="me-label">Subject</div>
        <div class="me-value">
          <span class="me-text">{{ email.subject }}</span>
          <el-button size="mini" type="text" icon="el-icon-document-copy" @click="copy(email.subject, 'Subject')">Copy</el-button>
        </div>
      </div>

      <div class="me-row" v-if="email.preheader">
        <div class="me-label">Preheader</div>
        <div class="me-value me-preheader">{{ email.preheader }}</div>
      </div>

      <div class="me-row" v-if="email.featured_card_ref">
        <div class="me-label">Featured card</div>
        <div class="me-value">
          <span class="me-card-tag">{{ email.featured_card_ref.card_type }}</span>
          <span class="me-card-idx">#{{ email.featured_card_ref.card_index }}</span>
          <span v-if="featuredCardHook" class="me-card-hook"> — {{ featuredCardHook }}</span>
        </div>
      </div>

      <div class="me-row" v-if="email.cta && email.cta.url">
        <div class="me-label">CTA</div>
        <div class="me-value">
          <a :href="email.cta.url" target="_blank" rel="noopener" class="me-cta-link">{{ email.cta.label || email.cta.url }}</a>
          <span class="me-cta-url">{{ email.cta.url }}</span>
        </div>
      </div>

      <div class="me-row me-body-row" v-if="email.body_html">
        <div class="me-label">Body preview</div>
        <div class="me-body-preview">
          <iframe :srcdoc="bodyHtmlWrapped" sandbox="" referrerpolicy="no-referrer"></iframe>
        </div>
      </div>

      <div class="me-actions">
        <el-button size="small" icon="el-icon-document-copy" :disabled="!email.body_markdown" @click="copy(email.body_markdown, 'Markdown')">Copy markdown</el-button>
        <el-button size="small" icon="el-icon-document-copy" :disabled="!email.body_html" @click="copy(email.body_html, 'HTML')">Copy HTML</el-button>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "MarketingEmailPanel",
  props: {
    collectionForm: { type: Object, required: true },
    collectionId: { type: String, default: "" },
  },
  computed: {
    email() {
      return this.collectionForm && this.collectionForm.marketing_email
        ? this.collectionForm.marketing_email
        : null;
    },
    hasContent() {
      return !!(this.email && (this.email.subject || this.email.body_markdown));
    },
    featuredCardHook() {
      const ref = this.email && this.email.featured_card_ref;
      if (!ref) return "";
      const cards = this.collectionForm.sns_cards || [];
      const card = cards[ref.card_index];
      return card && card.hook ? card.hook : "";
    },
    bodyHtmlWrapped() {
      // Wrap body_html in a minimal HTML doc with a soft default font so the iframe renders cleanly.
      const html = this.email.body_html || "";
      return `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;font-size:15px;line-height:1.55;color:#1a1a1a;margin:14px;}p{margin:0 0 14px}h2{font-size:18px;margin:20px 0 10px}blockquote{margin:14px 0;padding:8px 14px;border-left:3px solid #888;color:#555;font-style:italic;}a{color:#0a6cdf}</style></head><body>${html}</body></html>`;
    },
  },
  methods: {
    copy(text, label) {
      if (!text) return;
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) {}
        document.body.removeChild(ta);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`${label} copied`))
          .catch(() => { fallback(); this.$message.success(`${label} copied`); });
      } else {
        fallback();
        this.$message.success(`${label} copied`);
      }
    },
  },
};
</script>

<style scoped>
.me-panel { padding: 10px 4px; }
.me-empty { padding: 24px; text-align: center; color: #909399; font-style: italic; }
.me-row { display: flex; gap: 18px; padding: 10px 0; border-bottom: 1px solid #f0f2f5; }
.me-row:last-of-type { border-bottom: none; }
.me-label { flex: 0 0 130px; font-weight: 500; color: #606266; font-size: 13px; padding-top: 4px; }
.me-value { flex: 1; min-width: 0; }
.me-text { font-size: 14px; color: #303133; margin-right: 10px; }
.me-preheader { font-size: 13px; color: #606266; font-style: italic; }
.me-card-tag { display: inline-block; padding: 2px 8px; background: #ecf5ff; color: #409eff; border-radius: 3px; font-size: 12px; margin-right: 6px; }
.me-card-idx { font-size: 12px; color: #909399; }
.me-card-hook { font-size: 13px; color: #303133; }
.me-cta-link { color: #409eff; text-decoration: underline; font-weight: 500; margin-right: 10px; }
.me-cta-url { font-size: 12px; color: #909399; word-break: break-all; }
.me-body-row { flex-direction: column; gap: 8px; }
.me-body-row .me-label { flex: 0 0 auto; padding-top: 0; }
.me-body-preview { width: 100%; height: 460px; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; background: #fff; }
.me-body-preview iframe { width: 100%; height: 100%; border: 0; }
.me-actions { display: flex; gap: 10px; padding-top: 12px; }
</style>
